"use client";

import React, { useState, useEffect } from "react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";
import FloatingArrow from "./FloatingArrow";
import { useLocale } from "@/app/lib/useLocale";
import { useLandingCms } from "@/app/contexts/LandingCmsContext";

const TRADING_STRATEGY_STEP_ICONS = [
  "/images/trading-strat/hand1.png",
  "/images/trading-strat/hand2.png",
  "/images/trading-strat/hand3.png",
] as const;

const TradingStrategy: React.FC = () => {
  const t = useTranslations("TradingStrategy");
  const cms = useLandingCms();
  const locale = useLocale();
  const isChinese = locale === "zh";
  const [showArrows, setShowArrows] = useState(false);

  // Show arrows only after main content has appeared (fallback if section not in view)
  useEffect(() => {
    const fallback = setTimeout(() => setShowArrows(true), 2500);
    return () => clearTimeout(fallback);
  }, []);

  // Helper function to get translation with fallback
  const getTranslation = (key: string, fallback: string) => {
    const translated = t(key);
    if (
      translated === `TradingStrategy.${key}` ||
      translated.startsWith("TradingStrategy.")
    ) {
      return fallback;
    }
    return translated;
  };

  const defaultSteps = [
    {
      titleKey: "step1.title",
      titleFallback: "Describe the Idea",
      descriptionKey: "step1.description",
      descriptionFallback:
        "Describe your TradingView strategy rules in plain English.",
    },
    {
      titleKey: "step2.title",
      titleFallback: "Generate with AI",
      descriptionKey: "step2.description",
      descriptionFallback:
        "Generate TradingView-ready Pine Script automatically.",
    },
    {
      titleKey: "step3.title",
      titleFallback: "Live Validate on Charts",
      descriptionKey: "step3.description",
      descriptionFallback: "Preview and backtest on charts, then refine fast.",
    },
  ];

  const cmsSteps = cms?.tradingStrategySteps ?? [];
  const useCmsSteps =
    cmsSteps.length > 0 &&
    cmsSteps.some((step) => step.title?.trim() || step.description?.trim());

  const steps = defaultSteps.map((step, index) => {
    const cmsStep = useCmsSteps ? cmsSteps[index] : undefined;
    return {
      icon:
        TRADING_STRATEGY_STEP_ICONS[index] ??
        "/images/trading-strat/hand1.png",
      title:
        cmsStep?.title?.trim() ||
        getTranslation(step.titleKey, step.titleFallback),
      description:
        cmsStep?.description?.trim() ||
        getTranslation(step.descriptionKey, step.descriptionFallback),
    };
  });

  // Arrow positioning configurations
  // Default configuration (English and other languages)
  const arrow1Config = {
    left: {
      base: "10%",
      sm: "15%",
      md: "68%",
      lg: "22%",
      xl: "22%",
    },
    top: {
      base: "45%",
      md: "50%",
      lg: "55%",
    },
    width: {
      base: 150,
      sm: 100,
      md: 200,
      lg: 194,
      xl: 264,
    },
    height: {
      base: 30,
      sm: 40,
      md: 50,
      lg: 58,
    },
  };

  const arrow2Config = {
    left: {
      base: "10%",
      sm: "15%",
      md: "8%",
      lg: "55%",
      xl: "55%",
    },
    top: {
      base: "95%",
      sm: "95%",
      md: "80%",
      lg: "105%",
      xl: "105%",
    },
    width: {
      base: 150,
      sm: 200,
      md: 200,
      lg: 194,
      xl: 274,
    },
    height: {
      base: 30,
      sm: 40,
      md: 50,
      lg: 58,
    },
  };

  // Chinese-specific configuration
  const arrow1ConfigZh = {
    left: {
      base: "10%",
      sm: "15%",
      md: "68%",
      lg: "26%",
      xl: "26%",
    },
    top: {
      base: "45%",
      md: "50%",
      lg: "55%", // Adjusted for Chinese
    },
    width: {
      base: 150,
      sm: 100,
      md: 200,
      lg: 194,
      xl: 264,
    },
    height: {
      base: 30,
      sm: 40,
      md: 50,
      lg: 58,
    },
  };

  const arrow2ConfigZh = {
    left: {
      base: "10%",
      sm: "15%",
      md: "8%",
      lg: "55%",
      xl: "55%",
    },
    top: {
      base: "95%",
      sm: "95%",
      md: "80%",
      lg: "105%", // Adjusted for Chinese
      xl: "105%", // Adjusted for Chinese
    },
    width: {
      base: 150,
      sm: 200,
      md: 200,
      lg: 194,
      xl: 274,
    },
    height: {
      base: 30,
      sm: 40,
      md: 50,
      lg: 58,
    },
  };

  // Select configuration based on locale
  const arrow1 = isChinese ? arrow1ConfigZh : arrow1Config;
  const arrow2 = isChinese ? arrow2ConfigZh : arrow2Config;

  return (
    <LazyMotion features={domAnimation}>
    <section className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#191919] py-20 md:pb-24 lg:pb-40 pb-0">
      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 h-full flex flex-col justify-center">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <m.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-3 sm:mb-4 md:mb-6 text-2xl md:text-3xl lg:text-[40px] !leading-8 
            md:!leading-10 lg:!leading-[55px] text-[#F5F5F5] text-center mx-auto max-w-5xl"
            style={{
              fontStyle: "normal",
              fontWeight: 800,
            }}
          >
            <div>
              {cms?.tradingStrategyTitlePart1?.trim() ||
                getTranslation(
                  "titlePart1",
                  "How to write a Pine Script strategy",
                )}
            </div>
            <div className="text-[#767EF6]">
              {cms?.tradingStrategyTitlePart2?.trim() ||
                getTranslation("titlePart2", "in Three Simple Steps")}
            </div>
          </m.h2>

          <m.p
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center text-[#FCFAFA] mb-6 max-w-[780px] mx-auto px-4 md:text-lg lg:text-[24px] whitespace-pre-line !leading-6 md:!leading-7 lg:!leading-[40px]"
            style={{
              fontWeight: 600
            }}
          >
            {cms?.tradingStrategySubtitle?.trim() ||
              getTranslation(
                "subtitle",
                "No code. No setup. PineGen AI generates the Pine Script",
              )}
          </m.p>
        </div>

        {/* Steps Container */}
        <div className="relative w-full flex flex-col lg:flex-row items-center justify-center gap-14  md:gap-12 lg:gap-4 xl:gap-8 flex-1 md:mt-20 mt-[50px]">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              {/* Step Card */}
              <m.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                onAnimationComplete={
                  index === steps.length - 1
                    ? () => setShowArrows(true)
                    : undefined
                }
                className="flex flex-col items-center text-center w-full lg:w-auto max-w-sm lg:max-w-none flex-1 lg:flex-initial z-10"
              >
                {/* Icon */}
                <div className="mb-4 sm:mb-6 md:mb-8 w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 relative flex-shrink-0">
                  <Image
                    src={step.icon}
                    alt={`Step ${index + 1} icon`}
                    fill
                    className="object-contain"
                    sizes="(max-width: 640px) 64px, (max-width: 768px) 80px, (max-width: 1024px) 96px, 112px"
                  />
                </div>

                {/* Title */}
                <h3
                  className="text-[#C8CAF4] font-bold mb-2 sm:mb-3 md:mb-6"
                  style={{
                    fontSize: "clamp(18px, 3vw, 28px)",
                    lineHeight: "1.3",
                  }}
                >
                  {step.title}
                </h3>

                {/* Description */}
                <p
                  className="text-[#FCFAFA]  max-w-[250px] font-semibold   md:max-w-md lg:max-w-lg"
                  style={{
                    lineHeight: "1.5",
                    fontSize: "clamp(14px, 1.5vw, 18px)",
                  }}
                >
                  {step.description}
                </p>
              </m.div>

              {/* Arrow Connector - Only show between steps on desktop */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block flex-shrink-0 z-0">
                  {/* First arrow - use right arrow */}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Responsive usage - different values for each breakpoint */}

        {/* Arrows only mount and appear after step content has appeared */}
        {showArrows && (
          <m.div
            className="contents"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
             {/* Arrow between step 1 and step 2 */}
            <FloatingArrow
              src="/images/trading-strat/arr-r.png"
              left={arrow1.left}
              top={arrow1.top}
              width={arrow1.width}
              height={arrow1.height}
              rotate={{
                base: 0,
                md: 90,
                lg: 0,
              }}
              flipX={{
                base: false,
                lg: false,
                xl: false,
              }}
              flipY={{
                base: false,
                md: false,
                lg: false,
                xl: false,
              }}
              zIndex={{
                base: 1,
                lg: 15,
                xl: 10,
              }}
            />
            {/* Arrow between step 2 and step 3 */}
            <FloatingArrow
              src="/images/trading-strat/arr-r.png"
              left={arrow2.left}
              top={arrow2.top}
              width={arrow2.width}
              height={arrow2.height}
              rotate={{
                base: 180,
                md: 90,
                lg: 180,
              }}
              flipX={{
                base: true,
                md: false,
                lg: true,
                xl: true,
              }}
              flipY={{
                base: false,
                md: true,
                lg: false,
                xl: false,
              }}
              zIndex={{
                base: 1,
                lg: 15,
                xl: 10,
              }}
            />
          </m.div>
        )}
      </div>
    </section>
    </LazyMotion>
  );
};

export default TradingStrategy;
